// Copyright ©️ 2026 eVolve MEP, LLC

import { isNotNil } from 'helpers/isNotNil';

const { Autodesk } = window;

const PREV_PAGE_BUTTON_ID = '[PageNavExtension]_prev_btn';
const NEXT_PAGE_BUTTON_ID = '[PageNavExtension]_next_btn';
const PAGE_NUM_BUTTON_ID = '[PageNavExtension]_page_num_btn';

const PAGE_NUM_INPUT_ELEMENT_CLASSNAME = 'adsk-page-num-input';
const PAGE_NUM_TEXT_ELEMENT_CLASSNAME = 'adsk-page-num-text';
const BUTTON_DISABLED_CLASSNAME = 'autodesk-btn-disabled';

type PageNavExtensionOptions = Autodesk.Viewing.ExtensionOptions & {
  onLoadPage: (page: number) => void;
};

export class PageNavExtension extends Autodesk.Viewing.Extension {
  subToolbar: Autodesk.Viewing.UI.ControlGroup | null = null;
  currentPage = 1;
  totalPages = 0;
  /** Optimistically updated on every click so rapid clicks step correctly before `setPageInfo` confirms the load. */
  private pendingPage = 1;
  private readonly onLoadPage: (page: number) => void;
  private pageInput: HTMLInputElement | null = null;
  private pageText: HTMLParagraphElement | null = null;

  constructor(viewer: Autodesk.Viewing.GuiViewer3D, { onLoadPage, ...opts }: PageNavExtensionOptions) {
    super(viewer, opts);
    this.onLoadPage = onLoadPage;
  }

  load() {
    return true;
  }

  onToolbarCreated(toolbar: Autodesk.Viewing.UI.ToolBar) {
    const prevButton = new window.Autodesk.Viewing.UI.Button(PREV_PAGE_BUTTON_ID);
    prevButton.onClick = () => {
      if (this.pendingPage <= 1) return;
      this.onLoadPage(--this.pendingPage);
    };
    prevButton.addClass('autodesk-prev-page-btn');
    prevButton.setIcon('adsk-icon-arrow');
    prevButton.setToolTip('Previous');
    prevButton.addClass(BUTTON_DISABLED_CLASSNAME);

    const pageNumButton = new window.Autodesk.Viewing.UI.Button(PAGE_NUM_BUTTON_ID);
    pageNumButton.addClass('autodesk-page_num_btn');

    const nextButton = new window.Autodesk.Viewing.UI.Button(NEXT_PAGE_BUTTON_ID);
    nextButton.onClick = () => {
      if (this.pendingPage >= this.totalPages) return;
      this.onLoadPage(++this.pendingPage);
    };
    nextButton.addClass('autodesk-next-page-btn');
    nextButton.setIcon('adsk-icon-arrow');
    nextButton.setToolTip('Next');
    nextButton.addClass(BUTTON_DISABLED_CLASSNAME);

    this.subToolbar = new window.Autodesk.Viewing.UI.ControlGroup('page-navigation-toolbar');
    this.subToolbar.addControl(prevButton);
    this.subToolbar.addControl(pageNumButton);
    this.subToolbar.addControl(nextButton);
    toolbar.addControl(this.subToolbar, { index: 0 });

    // The button container is now in the DOM — create and attach the input and label once.
    // Scope lookup to this viewer's container so two simultaneous viewers don't collide on the same ID.
    const controlEl = this.viewer.container.querySelector(`[id="${PAGE_NUM_BUTTON_ID}"]`);
    if (isNotNil(controlEl)) {
      const inputEl = document.createElement('input');
      inputEl.type = 'number';
      inputEl.classList.add(PAGE_NUM_INPUT_ELEMENT_CLASSNAME);
      inputEl.value = String(this.currentPage);
      inputEl.addEventListener('keydown', this.onInputKeyDown.bind(this));
      inputEl.addEventListener('focusout', this.onInputFocusOut.bind(this));
      this.pageInput = inputEl;

      const textEl = document.createElement('p');
      textEl.classList.add(PAGE_NUM_TEXT_ELEMENT_CLASSNAME);
      textEl.innerText = `/ ${this.totalPages}`;
      this.pageText = textEl;

      controlEl.appendChild(inputEl);
      controlEl.appendChild(textEl);
    }
  }

  /** Called by the host component after each page load to sync button and input state. */
  setPageInfo(currentPage: number, totalPages: number) {
    this.currentPage = currentPage;
    this.pendingPage = currentPage;
    this.totalPages = totalPages;

    if (this.pageInput) this.pageInput.value = String(currentPage);
    if (this.pageText) this.pageText.innerText = `/ ${totalPages}`;

    if (this.subToolbar) {
      const prevBtn = this.subToolbar.getControl(PREV_PAGE_BUTTON_ID) as Autodesk.Viewing.UI.Control | undefined;
      if (currentPage <= 1) prevBtn?.addClass(BUTTON_DISABLED_CLASSNAME);
      else prevBtn?.removeClass(BUTTON_DISABLED_CLASSNAME);

      const nextBtn = this.subToolbar.getControl(NEXT_PAGE_BUTTON_ID) as Autodesk.Viewing.UI.Control | undefined;
      if (currentPage >= totalPages) nextBtn?.addClass(BUTTON_DISABLED_CLASSNAME);
      else nextBtn?.removeClass(BUTTON_DISABLED_CLASSNAME);
    }
  }

  private onInputKeyDown(event: KeyboardEvent) {
    const target = event.target as HTMLInputElement;
    const targetPage = Number(target.value);
    const isValid = targetPage > 0 && targetPage <= this.totalPages && Number.isInteger(targetPage);
    if (event.key === 'Escape') {
      target.value = String(this.currentPage);
    } else if (event.key === 'Enter') {
      if (!isValid) target.value = String(this.currentPage);
      else {
        this.pendingPage = targetPage;
        this.onLoadPage(targetPage);
      }
    }
  }

  private onInputFocusOut(event: FocusEvent) {
    const target = event.target as HTMLInputElement;
    const targetPage = Number(target.value);
    const isValid = targetPage > 0 && targetPage <= this.totalPages && Number.isInteger(targetPage);
    if (targetPage !== this.currentPage) {
      if (!isValid) target.value = String(this.currentPage);
      else {
        this.pendingPage = targetPage;
        this.onLoadPage(targetPage);
      }
    }
  }

  unload() {
    if (this.subToolbar) {
      this.viewer.toolbar.removeControl(this.subToolbar);
      this.subToolbar = null;
    }
    this.pageInput = null;
    this.pageText = null;
    return super.unload();
  }
}
